import axios, { type AxiosInstance, type AxiosRequestConfig, type AxiosResponse } from 'axios';

// 定义接口返回格式，根据实际后端约定修改
export interface Result<T = any> {
  code?: number;
  success?: boolean;
  message?: string;
  result?: T;
  data?: T;
}

// 简单的 Axios 封装
class VAxios {
  private axiosInstance: AxiosInstance;
  private readonly options: AxiosRequestConfig;

  constructor(options: AxiosRequestConfig) {
    this.options = options;
    this.axiosInstance = axios.create(options);
    this.setupInterceptors();
  }

  private setupInterceptors() {
    // 请求拦截器
    this.axiosInstance.interceptors.request.use(
      (config) => {
        // TODO: 这里可以添加 token 等逻辑
        // const token = getToken();
        // if (token) {
        //   config.headers.Authorization = token;
        // }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // 响应拦截器
    this.axiosInstance.interceptors.response.use(
      (response: AxiosResponse<Result>) => {
        const res = response.data;

        if (typeof res === 'object' && res !== null) {
          if ('success' in res && res.success === false) {
            return Promise.reject(new Error(res.message || 'Request Error'));
          }

          if ('code' in res && typeof res.code === 'number' && res.code !== 200) {
            return Promise.reject(new Error(res.message || 'Request Error'));
          }
        }

        // 兼容两种后端返回结构：{ result } / { data }
        if (typeof res === 'object' && res !== null) {
          if ('result' in res) return (res as any).result;
          if ('data' in res) return (res as any).data;
        }

        // 兜底返回原响应
        return res as any;
      },
      (error) => {
        // 处理错误，例如 401, 500 等
        const msg = error.response?.data?.message || error.message || 'Request Error';
        console.error('Request Error:', msg, error);
        return Promise.reject(error);
      }
    );
  }

  // 支持 get 方法
  get<T = any>(config: AxiosRequestConfig): Promise<T> {
    return this.request({ ...config, method: 'GET' });
  }

  // 支持 post 方法
  post<T = any>(config: AxiosRequestConfig): Promise<T> {
    return this.request({ ...config, method: 'POST' });
  }

  // 支持 put 方法
  put<T = any>(config: AxiosRequestConfig): Promise<T> {
    return this.request({ ...config, method: 'PUT' });
  }

  // 支持 delete 方法
  delete<T = any>(config: AxiosRequestConfig): Promise<T> {
    return this.request({ ...config, method: 'DELETE' });
  }

  request<T = any>(config: AxiosRequestConfig): Promise<T> {
    return this.axiosInstance.request(config);
  }
}

// 导出 defHttp 实例
export const defHttp = new VAxios({
  baseURL: import.meta.env.VITE_API_BASE_URL, // 使用环境变量配置的 baseURL
  timeout: 30 * 1000,
  headers: { 'Content-Type': 'application/json;charset=utf-8' },
});
